import React, { useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import CommonFormElement from "../../../components/UI/CommonFormElements/CommonFormElement";
import { useTranslation } from "react-i18next";
import StarRating from "../../../parhato2/src/entities/StarRating/StarRating";
import { IonIcon, IonSpinner, IonTextarea } from "@ionic/react";
import useAddReviewSetOrder from "../../../providers/Orders/useAddReviewSetOrder";
import { OrderReviewType } from "@core/types";
import classNames from "classnames/bind";
import styles from "./OrderReview.module.scss";
import { CustomBottomSheet } from "../../../components/UI/CustomBottomSheet";
import { star } from "ionicons/icons";
import useReviewSetOrderById from "../../../providers/Orders/useGetReviewSerOrderById";
import { getOrderReviewOverallRating } from "../../../utils/getOrderReviewOverallRating";

const cx = classNames.bind(styles);

type RateOrderFormValues = Pick<
  OrderReviewType,
  "productQuality" | "deliveryQuality" | "appUsability" | "customerNote"
>;

interface RateOrderProps {
  orderNumber: string;
  onSuccess: () => void;
}

const RateOrder = ({ orderNumber, onSuccess }: RateOrderProps) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RateOrderFormValues>({
    mode: "onBlur",
  });

  const [isOpen, setIsOpen] = useState(false);
  const productQuality = useWatch({ control, name: "productQuality" });
  const deliveryQuality = useWatch({ control, name: "deliveryQuality" });
  const appUsability = useWatch({ control, name: "appUsability" });

  const overallRating = useMemo(() => {
    if (!productQuality || !deliveryQuality || !appUsability) {
      return null;
    }
    return getOrderReviewOverallRating({
      productQuality,
      deliveryQuality,
      appUsability,
      orderId: orderNumber,
    });
  }, [productQuality, deliveryQuality, appUsability, orderNumber]);

  const { mutate: addReviewOrder, isPending } = useAddReviewSetOrder(orderNumber, {
    onSuccess: () => {
      setIsOpen(false);
      onSuccess();
    },
  });

  const orderReviewSetEnabled = !!orderNumber;
  const { data: orderReviewSet, isLoading: isLoadingOrderReviewSet } = useReviewSetOrderById(orderNumber, {
    enabled: orderReviewSetEnabled,
  });

  const isOrderReviewSetLoading = isLoadingOrderReviewSet && orderReviewSetEnabled;

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleFSubmit = async ({
    customerNote,
    productQuality,
    deliveryQuality,
    appUsability,
  }: RateOrderFormValues) => {
    const reviewData: OrderReviewType = {
      customerNote,
      productQuality,
      deliveryQuality,
      appUsability,
      orderId: orderNumber,
    };

    addReviewOrder(reviewData);
  };

  const areaPlaceholder = t(
    "Please rate the service and leave any comments or feedback that can help us make it better. The main criteria include the quality of the products, speed of delivery, courtesy of the delivery person and the fact of delivery to the door. Your feedback is very important to us!",
  );

  const ratingRequiredMessage = t("Needed to evaluate the order");

  const ratingCategories = [
    {
      name: "productQuality" as const,
      label: t("Product quality rating"),
      ratingName: "product-quality",
    },
    {
      name: "deliveryQuality" as const,
      label: t("Delivery quality rating"),
      ratingName: "delivery-quality",
    },
    {
      name: "appUsability" as const,
      label: t("App usability rating"),
      ratingName: "app-usability",
    },
  ];

  const isDisabled =
    isPending || !!orderReviewSet || isOrderReviewSetLoading || !productQuality || !deliveryQuality || !appUsability;

  return (
    <div className="flex flex-col justify-between ">
      <CustomBottomSheet
        open={isOpen}
        onDismiss={handleClose}
        showDefaultHeader={false}
        contentClassName={cx("bottom_sheet", "flex flex-col items-center")}
        snapPoints={({ minHeight }) => [minHeight + 100, minHeight + 100]}
        renderPreview={() => (
          <button onClick={handleOpen} className="flex flex-col items-center max-w-[5.625rem] text-red-500">
            <IonIcon icon={star} className=" text-2xl p-3 rounded-full bg-gray-100 " />
            <span className="text-sm text-center">{t("Rate the order")}</span>
          </button>
        )}
        footer={
          <button
            disabled={isDisabled}
            onClick={() => handleSubmit(handleFSubmit)()}
            className="w-full h-full font-bold py-2.5 bg-primary disabled:opacity-50  disabled:cursor-not-allowed"
          >
            {isPending ? <IonSpinner name="crescent" className="animate-spin w-4 h-4" /> : t("Submit")}
          </button>
        }
      >
        <form className="h-full mx-2.5" onSubmit={handleSubmit(handleFSubmit)}>
          <div className="flex flex-col justify-between h-full relative gap-2.5">
            <div className="flex flex-col ">
              <div className="text-[30px] text-center">{t("Rate the order")}</div>
              {overallRating != null && (
                <div className="text-sm text-center text-gray-500 mt-1">
                  {t("Overall rating")}: {overallRating} {t("out of 5")}
                </div>
              )}
            </div>
            <div className={cx("rateWrapper", "flex flex-col items-stretch gap-4 w-full")}>
              {ratingCategories.map(({ name, label, ratingName }) => (
                <Controller
                  key={name}
                  name={name}
                  control={control}
                  rules={{
                    required: true,
                    min: 1,
                    max: 5,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <CommonFormElement
                      commonLabelClassName="!p-0"
                      label={label}
                      isValid={(value ?? 0) > 0}
                      errorMessage={errors[name]?.message}
                    >
                      <StarRating name={ratingName} onChange={onChange} value={value ?? 0} />
                    </CommonFormElement>
                  )}
                />
              ))}
            </div>

            <Controller
              name="customerNote"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <CommonFormElement
                    commonLabelClassName="!p-0 !hidden"
                    classListItem="mb-0"
                    isValid={(value?.trim().length ?? 0) > 0}
                    errorMessage={errors.customerNote?.message}
                  >
                    <IonTextarea
                      inputMode="text"
                      value={value}
                      placeholder={areaPlaceholder}
                      className="px-4 py-2 text-sm rounded-t-3xl border-1 border-solid min-h-[300px]"
                      onIonChange={(e) => onChange(e.detail.value!)}
                    />
                  </CommonFormElement>
                );
              }}
            />
          </div>
        </form>
      </CustomBottomSheet>
    </div>
  );
};

export default RateOrder;
